import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Database, Globe, UserCheck } from 'lucide-react';

const TranslatorPrivacy = () => {
  const sections = [
    {
      icon: Database,
      title: "Information We Collect",
      content: [
        "Translation text and voice input for processing purposes",
        "App usage analytics to improve user experience",
        "Device information for compatibility optimization",
        "Crash reports to enhance app stability"
      ]
    },
    {
      icon: Lock,
      title: "How We Use Your Information",
      content: [
        "Process translations using Google ML Kit services",
        "Save translation history locally on your device",
        "Improve translation accuracy and app performance",
        "Provide customer support when needed"
      ]
    },
    {
      icon: Shield,
      title: "Data Protection",
      content: [
        "All translations are processed securely",
        "No personal data is stored on external servers",
        "Translation history remains on your device",
        "Industry-standard encryption for data transmission"
      ]
    },
    {
      icon: Eye,
      title: "Information Sharing",
      content: [
        "We do not sell or share personal information with third parties",
        "Google ML Kit processes translations according to their privacy policy",
        "Anonymous usage statistics may be shared for app improvement",
        "Legal compliance when required by law"
      ]
    },
    {
      icon: UserCheck,
      title: "Your Rights",
      content: [
        "Delete your translation history at any time",
        "Control what data the app can access",
        "Opt out of analytics collection",
        "Request data deletion by contacting us"
      ]
    },
    {
      icon: Globe,
      title: "Third-Party Services",
      content: [
        "Google ML Kit for translation processing",
        "Google Play Services for app functionality",
        "Analytics services for usage insights",
        "All services comply with their respective privacy policies"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-20 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how Quick Translator protects and handles your data.
          </p>
          <div className="mt-6 text-sm text-gray-500">
            Last updated: August 1, 2025
          </div>
        </motion.div>

        {/* Introduction */}
        <motion.div 
          className="bg-white rounded-2xl p-8 shadow-lg mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Privacy</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Quick Translator is committed to protecting your privacy and ensuring the security of your personal information. 
            This privacy policy explains how we collect, use, and protect your data when you use our translation application.
          </p>
          <p className="text-gray-600 leading-relaxed">
            By using Quick Translator, you agree to the collection and use of information in accordance with this policy. 
            We will not use or share your information except as described in this privacy policy.
          </p>
        </motion.div>

        {/* Privacy Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-6">
                  <section.icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.content.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Information */}
        <motion.div 
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 mt-12 text-white"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Questions About Your Privacy?</h3>
            <p className="text-blue-100 mb-6 leading-relaxed">
              If you have any questions about this privacy policy or how we handle your data, 
              please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="mailto:hu477418@gmail.com"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors duration-300"
              >
                Contact Support
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.suuin.quicktranslator"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-blue-600 transition-colors duration-300"
              >
                View on Google Play
              </a>
            </div>
          </div>
        </motion.div>

        {/* Data Rights Summary */}
        <motion.div 
          className="bg-white rounded-2xl p-8 shadow-lg mt-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Your Data Rights Summary</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">✓ You Control Your Data</h4>
              <p className="text-gray-600 text-sm">All translation history is stored locally on your device and can be deleted at any time.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">✓ No Selling of Data</h4>
              <p className="text-gray-600 text-sm">We never sell your personal information to third parties or advertisers.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">✓ Transparent Processing</h4>
              <p className="text-gray-600 text-sm">We clearly explain how your data is used for translation services.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">✓ Security First</h4>
              <p className="text-gray-600 text-sm">Industry-standard encryption protects your data during transmission.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TranslatorPrivacy; 